<script lang="ts" setup>
import { watch } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useRuntimeConfig } from '#app'
import type { GlobImageI } from '~/utils'
import { getImageURL } from '~/utils'
import { accountMenus } from '~/resources/header/navigation-menu'
import { useNavigationBar } from '~/composables/common/use-navigation-bar'
import { useLogin } from '~/composables/account/use-login'
import { ModalCollection } from '~/constants'
import { AccountTabs } from '~/constants/account'
import { useModal } from '~/composables/common/use-modal'
import { useLogout } from '~/composables/account/use-logout'
import { useUserData } from '~composables/account/use-user-data'
import { useLighthouse } from '~/composables/common/use-lighthouse'

const { isLighthouseBot } = useLighthouse()
const { redirectMenu, isDropDownUser, userMenuRef, toggleDropDownUser, mouseOverDropDown, isShowWallet, route } =
  useNavigationBar()
const { isLogged, showModalLogin } = useLogin()
const { openModal } = useModal()
const { currentUser, avatar, currentBalanceFormat } = useUserData()
const { submitLogout } = useLogout()
const config = useRuntimeConfig()
const MAIN_CREDIT_UNIT = config.public.MAIN_CREDIT_UNIT
watch(
  () => userMenuRef.value,
  (menuRef: HTMLElement) => {
    if (menuRef) {
      onClickOutside(userMenuRef, () => {
        isDropDownUser.value = false
        isShowWallet.value = false
      })
    }
  },
  { immediate: true }
)
const globImages: GlobImageI = import.meta.glob('~/assets/images/logo/header/*', { eager: true })
const iconImgUrl = '/assets/images/logo/header'

const globImageSVG: GlobImageI = import.meta.glob('~/assets/images/icons/*', {
  eager: true
})
const logoutSVGUrl = '/assets/images/icons'
</script>
<template>
  <header id="header">
    <div class="container">
      <div class="header header__navigation">
        <div class="header__wrapper-logo">
          <NuxtLink to="/" class="logo cursor-pointer">
            <img v-if="!isLighthouseBot" :src="getImageURL(globImages, 'logo', iconImgUrl, 'webp')" alt="Logo Header" />
          </NuxtLink>
        </div>
        <div class="header__account">
          <ClientOnly>
            <div v-if="isLogged" class="header__box-account flex-box">
              <div class="d-flex cursor-pointer position-relative">
                <div class="header-user-data header-toggle-account flex-box show-desktop">
                  <div
                    class="gap-8 flex-box header-user-wrapper"
                    @mouseover="mouseOverDropDown"
                    @click="mouseOverDropDown"
                    @mouseleave="isShowWallet = false"
                  >
                    <div class="header-user-data__avatar hover-shine">
                      <ClientOnly>
                        <img
                          :src="avatar"
                          alt="avatar"
                          width="48"
                          height="48"
                          @click.prevent="redirectMenu(accountMenus[0])"
                        />
                      </ClientOnly>
                    </div>
                    <div class="header-user-data__fullname" @click.prevent="redirectMenu(accountMenus[0])">
                      <div class="text fs-14 fw-700 text-truncate">{{ currentUser.fullname }}</div>
                      <div class="amount flex-box">
                        <img class="icon-wallet" src="~/assets/images/header/wallet-mb-icon.png" alt="wallet Card" />
                        <span class="text-amount fs-16 fw-700 text-truncate">***</span>
                      </div>
                    </div>
                    <div class="wallet-dropdown" :class="{ active: isShowWallet }">
                      <div class="wallet-dropdown__item">
                        <span class="wallet-dropdown__wallet-name">Ví Chính</span>
                        <span class="wallet-dropdown__wallet-amount fs-14 fw-700">
                          {{ currentBalanceFormat }} {{ MAIN_CREDIT_UNIT }}
                        </span>
                      </div>
                      <div
                        v-for="indirectBrand in [config.public.INDIRECT_BRAND_NAME, config.public.INDIRECT_BRAND2_NAME]"
                        :key="indirectBrand"
                        class="wallet-dropdown__item"
                      >
                        <span class="wallet-dropdown__wallet-name">Ví {{ indirectBrand }}</span>
                        <NuxtLink
                          :to="AccountTabs.DEPOSIT"
                          class="wallet-dropdown__wallet-amount fs-14 fw-400 text-underline"
                        >
                          Nạp Tiền
                        </NuxtLink>
                      </div>
                    </div>
                  </div>
                  <div ref="userMenuRef" :class="{ active: isDropDownUser }">
                    <button class="btn-arrow d-flex align-items-center" @click="toggleDropDownUser">
                      <span class="icon-dropdown"></span>
                    </button>
                    <div class="account-menu" :class="{ open: isDropDownUser }">
                      <ul class="account-menu__nav p-0">
                        <li
                          v-for="(item, index) in accountMenus"
                          :key="index"
                          class="item"
                          @click.prevent="redirectMenu(item)"
                        >
                          <div class="menu-link" :class="{ active: route.fullPath === item.link }">
                            <img class="icon-menu" :src="item.icon" :alt="item.label" />
                            <span class="text-name">{{ item.label }}</span>
                          </div>
                        </li>
                        <li class="item">
                          <div class="btn-logout" @click="submitLogout()">
                            <img
                              :src="getImageURL(globImageSVG, 'ic-logout', logoutSVGUrl, 'svg')"
                              alt="Đăng xuất"
                              width="24"
                              height="24"
                              style="margin-right: 10px"
                            />
                            Đăng xuất
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div class="wallet-box">
                <NuxtLink to="/account?tab=transfer-money" class="wallet-box__icon">
                  <img src="~/assets/images/header/wallet-icon.png" alt="Wallet Icon" width="48" height="52" />
                </NuxtLink>
              </div>
              <div class="header__login-form">
                <NuxtLink :to="AccountTabs.DEPOSIT" class="button main main--login-deposit cursor-pointer">
                  <img
                    src="~/assets/images/header/deposit-icon.svg"
                    alt="withdraw"
                    width="20"
                    height="20"
                    style="margin-right: 10px"
                  />Nạp tiền
                </NuxtLink>
                <NuxtLink to="/account?tab=withdraw" class="button main main--primary cursor-pointer">
                  <img
                    src="~/assets/images/header/withdraw-icon.svg"
                    alt="withdraw"
                    width="20"
                    height="20"
                    style="margin-right: 10px"
                  />
                  Rút tiền
                </NuxtLink>
              </div>
            </div>
            <div v-else class="header__login-form">
              <div class="button main main--primary-outline cursor-pointer" @click="showModalLogin()">Đăng nhập</div>
              <div
                class="button main main--primary cursor-pointer"
                @click="openModal(ModalCollection.LOGIN_AND_SIGN_UP, ModalCollection.SIGN_UP, true)"
              >
                Đăng ký
              </div>
            </div>
          </ClientOnly>
        </div>
      </div>
    </div>
  </header>
</template>
<style lang="scss" scoped src="~scssAsset/desktop/layouts/header/index.scss"></style>
